import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';
import { getLoyaltyConfig, saveLoyaltyConfig } from '../services/loyaltyService';
import { DEFAULT_LOYALTY_CONFIG, validateLoyaltyConfig } from '../../../domain/loyaltyCalculations';
import { AuditLogger } from '../../../utils/auditLogger';

export function useLoyaltyConfig() {
  const { user } = useAuth();
  const { t } = useTranslation('settings');

  if (!user?.businessId) throw new Error('[useLoyaltyConfig] businessId zorunlu');

  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLoyaltyConfig(user.businessId, true)
      .then((cfg) => {
        if (cancelled) return;
        setConfig(JSON.parse(JSON.stringify(cfg)));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Loyalty config fetch error:', err);
        setLoadError(t('loyalty.loadError') + ': ' + err.message);
        setConfig(JSON.parse(JSON.stringify(DEFAULT_LOYALTY_CONFIG)));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.businessId]);

  const validation = useMemo(() => {
    if (!config) return { isValid: true, errors: [] };
    return validateLoyaltyConfig(config);
  }, [config]);

  const updateField = (key, value) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const updateTier = (idx, key, value) => {
    setConfig((prev) => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) => (i === idx ? { ...tier, [key]: value } : tier)),
    }));
    setDirty(true);
  };

  const addTier = () => {
    setConfig((prev) => {
      const lastTier = prev.tiers[prev.tiers.length - 1];
      return {
        ...prev,
        tiers: [
          ...prev.tiers,
          {
            name: `tier_${prev.tiers.length + 1}`,
            minLifetime: lastTier ? Number(lastTier.minLifetime) + 1000 : 0,
            multiplier: 1.0,
            color: '#888888',
          },
        ],
      };
    });
    setDirty(true);
  };

  const removeTier = (idx) => {
    if (config.tiers.length <= 1) {
      toast.warning(t('loyalty.minOneTier'));
      return;
    }
    setConfig((prev) => ({ ...prev, tiers: prev.tiers.filter((_, i) => i !== idx) }));
    setDirty(true);
  };

  const resetToDefaults = () => {
    if (!window.confirm(t('loyalty.resetConfirm'))) return;
    setConfig(JSON.parse(JSON.stringify(DEFAULT_LOYALTY_CONFIG)));
    setDirty(true);
    toast.info(t('loyalty.resetSuccess'));
  };

  const handleSave = async () => {
    if (!validation.isValid) { toast.error(t('loyalty.saveValidationError')); return; }
    if (!window.confirm(t('loyalty.saveConfirm'))) return;

    setSaving(true);
    try {
      await saveLoyaltyConfig(config, user.uid, user.businessId);
      try {
        await AuditLogger.log(user.uid, 'update', 'config', 'loyalty', {
          earnRate: config.earnRate, redeemRate: config.redeemRate,
          minRedeem: config.minRedeem, expirationMonths: config.expirationMonths,
          tierCount: config.tiers.length, tierNames: config.tiers.map((tier) => tier.name),
        });
      } catch (auditErr) {
        console.error('Audit log başarısız:', auditErr);
      }
      toast.success(t('loyalty.saveSuccess'));
      setDirty(false);
    } catch (err) {
      console.error('Save error:', err);
      if (err.code === 'invalid-config' && Array.isArray(err.details)) {
        toast.error(t('loyalty.saveError') + ':\n' + err.details.join('\n'));
      } else {
        toast.error(t('loyalty.saveError') + ': ' + err.message);
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    config, loading, saving, loadError, dirty, validation,
    updateField, updateTier, addTier, removeTier, resetToDefaults, handleSave,
  };
}
